import type { RefObject } from 'react';
import { useEffect, useState } from 'react';

type UseBufferOptions = {
  videoRef: RefObject<HTMLVideoElement>;
  startTime: number;
  endTime: number;
};

export const useBuffer = ({
  videoRef,
  startTime,
  endTime,
}: UseBufferOptions) => {
  const [loadedPercentage, setLoadedPercentage] = useState(0);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    const updateBuffer = () => {
      if (video.duration > 0) {
        const buffered = video.buffered;
        if (buffered.length > 0) {
          const bufferedEnd = buffered.end(buffered.length - 1);

          let percentage = 0;

          if (bufferedEnd <= startTime) {
            percentage = 0;
          } else if (bufferedEnd >= endTime) {
            percentage = 100;
          } else {
            const bufferedInRange = bufferedEnd - startTime;
            const constrainedDuration = endTime - startTime;
            percentage = (bufferedInRange / constrainedDuration) * 100;
          }

          setLoadedPercentage(Math.min(Math.max(percentage, 0), 100));
        }
      }
    };

    video.addEventListener('progress', updateBuffer);
    video.addEventListener('canplay', updateBuffer);
    video.addEventListener('canplaythrough', updateBuffer);
    video.addEventListener('loadedmetadata', updateBuffer);

    updateBuffer();

    return () => {
      video.removeEventListener('progress', updateBuffer);
      video.removeEventListener('canplay', updateBuffer);
      video.removeEventListener('canplaythrough', updateBuffer);
      video.removeEventListener('loadedmetadata', updateBuffer);
    };
  }, [videoRef, startTime, endTime]);

  return { loadedPercentage };
};
